import {
  User,
  Building2,
  Users,
  Bell,
  Clock,
  Shield,
  Fingerprint,
  Key,
  CreditCard,
  ChevronRight,
  Palette,
  MessageSquare,
  FileKey,
  Archive,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { cn } from "@/lib/utils";
import { OnboardingSettingsCard } from "@/pages/onboarding/components";
import { useAuthStore } from "@/store/auth.store";

const useSettingsSections = () => {
  const { t } = useTranslation();

  return [
    {
      id: "account",
      title: t("settings.sections.account"),
      description: t("settings.sections.accountDesc"),
      items: [
        {
          id: "profile",
          label: t("settings.nav.profile"),
          description: t("settings.profileDesc"),
          icon: User,
          path: "/settings/profile",
        },
        {
          id: "organisation",
          label: t("settings.nav.organisation"),
          description: t("settings.organisationDesc"),
          icon: Building2,
          path: "/settings/organisation",
          requiredRole: ["owner", "admin"],
        },
        {
          id: "team",
          label: t("settings.nav.teamMembers"),
          description: t("settings.teamMembersDesc"),
          icon: Users,
          path: "/settings/team",
          requiredRole: ["owner", "admin"],
        },
        {
          id: "branding",
          label: t("settings.nav.branding"),
          description: t("settings.brandingDesc"),
          icon: Palette,
          path: "/settings/branding",
          requiredRole: ["owner", "admin"],
          badge: t("badges.pro"),
        },
      ],
    },
    {
      id: "preferences",
      title: t("settings.sections.preferences"),
      description: t("settings.sections.preferencesDesc"),
      items: [
        {
          id: "notifications",
          label: t("settings.nav.notifications"),
          description: t("settings.notificationsDesc"),
          icon: Bell,
          path: "/settings/notifications",
        },
        {
          id: "reminders",
          label: t("settings.nav.defaultReminders"),
          description: t("settings.remindersDesc"),
          icon: Clock,
          path: "/settings/reminders",
        },
        {
          id: "sms",
          label: t("settings.nav.smsNotifications"),
          description: t("settings.smsDesc"),
          icon: MessageSquare,
          path: "/settings/sms",
          requiredRole: ["owner", "admin"],
        },
      ],
    },
    {
      id: "security",
      title: t("settings.sections.security"),
      description: t("settings.sections.securityDesc"),
      items: [
        {
          id: "security",
          label: t("settings.nav.security"),
          description: t("settings.securityItemDesc"),
          icon: Shield,
          path: "/settings/security",
        },
        {
          id: "sso",
          label: t("settings.sso.title"),
          description: t("settings.ssoDesc"),
          icon: Fingerprint,
          path: "/settings/sso",
          requiredRole: ["owner", "admin"],
          badge: t("badges.enterprise"),
        },
        {
          id: "advanced-security",
          label: t("settings.nav.advancedSecurity"),
          description: t("settings.advancedSecurityDesc"),
          icon: Key,
          path: "/settings/advanced-security",
          requiredRole: ["owner", "admin"],
          badge: t("badges.enterprise"),
        },
        {
          id: "certificates",
          label: "Digital Signatures",
          description: "Manage PDF signing certificates",
          icon: FileKey,
          path: "/settings/certificates",
          requiredRole: ["owner", "admin"],
        },
        {
          id: "retention",
          label: "Data Retention",
          description: "Automatic archiving and deletion policies",
          icon: Archive,
          path: "/settings/retention",
          requiredRole: ["owner", "admin"],
        },
      ],
    },
    {
      id: "billing",
      title: t("settings.sections.billing"),
      description: t("settings.sections.billingDesc"),
      items: [
        {
          id: "billing",
          label: t("settings.nav.subscription"),
          description: t("settings.subscriptionDesc"),
          icon: CreditCard,
          path: "/settings/billing",
          requiredRole: ["owner", "admin"],
        },
      ],
    },
  ];
};

function SettingsItem({ item, isActive, isMobile }) {
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      className={cn(
        "flex items-center gap-4 p-4 rounded-lg border transition-all hover:bg-accent hover:border-accent-foreground/20 active:bg-accent/80",
        isActive && "bg-accent border-primary",
        isMobile && "touch-target",
      )}
    >
      <div
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
          isActive ? "bg-primary text-primary-foreground" : "bg-muted",
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate">{item.label}</p>
          {item.badge && (
            <Badge variant="secondary" className="text-xs">
              {item.badge}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground truncate">
          {item.description}
        </p>
      </div>
      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
    </Link>
  );
}

export default function Settings() {
  const { t } = useTranslation();
  const settingsSections = useSettingsSections();
  const { user } = useAuthStore();
  const location = useLocation();
  const isMobile = useIsMobile();

  const userRole = user?.isOwner
    ? "owner"
    : user?.organisationRole || user?.teamRole || "member";

  const filterItemsByRole = (items) =>
    items.filter((item) => {
      if (!item.requiredRole) return true;
      if (user?.isOwner) return true;
      return item.requiredRole.includes(userRole);
    });

  const isActivePath = (path) => location.pathname === path;

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("settings.title")}
        </h1>
        <p className="text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      <div className="grid gap-6">
        {settingsSections.map((section) => {
          const filteredItems = filterItemsByRole(section.items);
          if (filteredItems.length === 0) return null;

          return (
            <Card key={section.id}>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">{section.title}</CardTitle>
                <CardDescription>{section.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  className={cn("grid gap-3", !isMobile && "sm:grid-cols-2")}
                >
                  {filteredItems.map((item) => (
                    <SettingsItem
                      key={item.id}
                      item={item}
                      isActive={isActivePath(item.path)}
                      isMobile={isMobile}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Help & Support section with Onboarding */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">
              {t("settings.sections.help", "Help & Support")}
            </CardTitle>
            <CardDescription>
              {t(
                "settings.sections.helpDesc",
                "Tutorials and support resources",
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <OnboardingSettingsCard />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
